import React, { useState, useEffect } from "react";
import { Formik, Form as FormikForm, Field } from "formik";
import * as Yup from "yup";
import { Form, Button, Row, Col } from "react-bootstrap";

const formatTimeToDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);

  // Get individual components
  const day = String(date.getDate()).padStart(2, "0"); // Ensure two digits
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0"); // Ensure two digits
  const minutes = String(date.getMinutes()).padStart(2, "0"); // Ensure two digits

  return `${date.toLocaleDateString("en-GB")}`;
};

const FormField = ({ field, errors, touched }) => (
  <Form.Group
    controlId={field.name}
    className="d-flex fieldWrapper align-items-center custom-no-error-icon mb-3"
  >
    <Form.Label className="dynamicFormTitle">
      {field.label} {field.isRequired && "*"}:
    </Form.Label>

    {field.type === "select" ? (
      <Field
        as="select"
        disabled={field.isDisabled}
        name={field.name}
        className={`form-control d-block text-right ${
          errors[field.name] && touched[field.name]
            ? "custom-invalid-select"
            : field.isDisabled
            ? "disabled-dropdown"
            : "enabled-dropdown"
        }`}
        style={{
          width: "100% !important",
          padding: "0.375rem 0.60rem",
        }}
      >
        {field.options.map((option, index) => (
          <option key={`opt-${index}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </Field>
    ) : field.type === "text" ? (
      <Field name={field.name}>
        {({ field: formikField, meta }) => {
          const dir = /[\u0590-\u05FF]/.test(formikField.value) ? "rtl" : "ltr";
          const textAlign = dir === "rtl" ? "right" : "left";

          return (
            <input
              {...formikField}
              disabled={field.isDisabled}
              className={`form-control textarea-form ${
                meta.touched && meta.error ? "is-invalid" : ""
              }`}
              style={{
                background: field.isDisabled ? "#dfe5ee" : "#cddaef",
                direction: dir,
                textAlign: textAlign,
              }}
            />
          );
        }}
      </Field>
    ) : field.type === "textarea" ? (
      <Field
        //as="textarea"
        name={field.name}
      >
        {({ field: formikField, meta }) => {
          const dir = /[\u0590-\u05FF]/.test(formikField.value) ? "rtl" : "ltr";
          const textAlign = dir === "rtl" ? "right" : "left";

          return (
            <textarea
              {...formikField}
              disabled={field.isDisabled}
              className={`form-control textarea-form ${
                meta.touched && meta.error ? "is-invalid" : ""
              }`}
              style={{
                background: field.isDisabled ? "#dfe5ee" : "#cddaef",
                direction: dir,
                textAlign: textAlign,
              }}
            />
          );
        }}
      </Field>
    ) : field.type === "number" ? (
      <Field
        disabled={field.isDisabled}
        type="text"
        name={field.name}
        className={`form-control custom-form-control ${
          errors[field.name] && touched[field.name] ? "is-invalid" : ""
        }`}
        pattern="[0-9]*"
        inputMode="numeric"
        style={{
          direction: "ltr",
          textAlign: "right",
        }}
        onInput={(e) => {
          e.target.value = e.target.value.replace(/[^0-9]/g, "");
        }}
      />
    ) : field?.type === "date" && typeof field.value === "string" ? (
      <Field
        disabled={field.isDisabled}
        type="text"
        name={field.name}
        value={field.value ?? ""}
        className={`form-control date-text-filter custom-form-control ${
          errors[field.name] && touched[field.name] ? "is-invalid" : ""
        }`}
      />
    ) : field?.type === "date" ? (
      <Field
        disabled={field.isDisabled}
        type={field.type}
        name={field.name}
        className={`form-control date-text-filter custom-form-control ${
          errors[field.name] && touched[field.name] ? "is-invalid" : ""
        }`}
        value={field.value ?? ""}
      />
    ) : (
      <Field
        disabled={field.isDisabled}
        type={field.type}
        name={field.name}
        style={{
          direction: "rtl",
          textAlign: "right",
        }}
        className={`form-control custom-form-control ${
          errors[field.name] && touched[field.name] ? "is-invalid" : ""
        }`}
      />
    )}
  </Form.Group>
);

const FormButtons = ({
  values,
  validateForm,
  setTouched,
  onClickSubmitForm,
  onClickCloseForm,
  resetForm,
}) => (
  // <div className="d-grid gap-2 pl-4">
  <div className="main-btn-wrap">
    <Button
      type="button"
      variant="primary"
      className="maxContentBtn footerbtn primaryBtn"
      size="sm"
      onClick={() =>
        onClickSubmitForm(values, validateForm, setTouched, true, resetForm)
      }
    >
      קליטת תמ"ר לבדיקות
    </Button>
    <Button
      type="button"
      variant="primary"
      className="maxContentBtn footerbtn primaryBtn"
      size="sm"
      onClick={() =>
        onClickSubmitForm(values, validateForm, setTouched, false, resetForm)
      }
    >
      {/* שמירת פקוייקט */}
      שמירת פרוייקט
    </Button>
    <Button
      type="button"
      variant="secondary"
      className="maxContentBtn footerbtn secondaryBtn"
      size="sm"
      onClick={() => onClickCloseForm(resetForm)}
    >
      סגירה
    </Button>
  </div>
);

const getDirection = (text) => {
  const rtlCharRegex = /[\u0590-\u05FF]/; // Hebrew range
  return rtlCharRegex.test(text) ? "rtl" : "ltr";
};

const CreateProjectForm = ({
  fields,
  onClickCloseForm = () => {},
  onClickSubmitForm = () => {},
}) => {
  const validationSchema = Yup.object().shape(
    fields.reduce((schema, field) => {
      if (field.isRequired) {
        schema[field.name] = Yup.string().required("שדה חובה");
      }
      return schema;
    }, {})
  );

  const initialValues = fields.reduce((values, field) => {
    const userDataString = sessionStorage.getItem("userData");
    let displayName;

    if (userDataString) {
      const userData = JSON.parse(userDataString);
      displayName = userData.user.displayName;
    }

    if (displayName && field.name === "CreatedBy")
      values[field.name] = displayName;
    else values[field.name] = "";
    return values;
  }, {});
  const sortByIndex = (items) => {
    return [...items].sort((a, b) => a.sequence - b.sequence);
  };

  fields = sortByIndex(fields);
  const columns = 3;
  const rowsPerColumn = Math.ceil(fields.length / columns);
  const fieldGroups = Array.from({ length: columns }, (_, col) =>
    fields.slice(col * rowsPerColumn, (col + 1) * rowsPerColumn)
  );

  const maxRows = Math.max(...fieldGroups.map((group) => group.length));

  fieldGroups.forEach((group) => {
    while (group.length < maxRows) {
      group.push(null);
    }
  });

  return (
    <div className="relative -mt-5 pt-10">
    <div className="" id="wellcomeDiv">
      <div className="workAreaPageAnimation main-wrap-loader" style={{ minHeight: 'calc(84vh + 8px)' }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
        >
          {({
            values,
            errors,
            touched,
            validateForm,
            setTouched,
            resetForm,
          }) => (
            <FormikForm dir={"rtl"} className="form-wrap-align-between">
              {/* <div className="create-project-form-wrap"> */}
              <div id="TamarTitleDiv">
                <p className="Tamar_Title">פרויקט</p>
                <Row className="customFormRow">
                  {fieldGroups.map((group, colIndex) => (
                    <Col md={4} key={`col-${colIndex}`} className="pr-2 pl-2">
                      {group.map((field, rowIndex) =>
                        field ? (
                          <FormField
                            key={`field-${colIndex}-${rowIndex}`}
                            field={field}
                            errors={errors}
                            touched={touched}
                          />
                        ) : (
                          <div
                            key={`empty-${colIndex}-${rowIndex}`}
                            className="d-flex align-items-center mb-3 dynamicFormPlaceHolderDiv"
                          />
                        )
                      )}
                    </Col>
                  ))}
                </Row>
              </div>
              <FormButtons
                values={values}
                validateForm={validateForm}
                setTouched={setTouched}
                onClickSubmitForm={onClickSubmitForm}
                onClickCloseForm={onClickCloseForm}
                resetForm={resetForm}
              />
              {/* </div> */}
            </FormikForm>
          )}
        </Formik>
      </div>
    </div>
    </div>
  );
};

export default CreateProjectForm;
